import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  CircularProgress,
  Dialog,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  type SxProps,
  TextField,
  type Theme,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Image } from '@realadvisor/image';
import { ArrowUpIcon, SaveIcon, SparklesIcon, XIcon } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { ActionPicker } from '@modules/_shared/components/image-gallery/enhance/ActionPicker';
import type {
  EnhanceImagePanelProps,
  EnhancePanelAction,
  EnhanceSubcategory,
  EnhancedVersion,
} from '@modules/_shared/components/image-gallery/enhance/enhance-image-types';
import {
  base64ToBlobUrl,
  resizeImageToBase64,
} from '@modules/_shared/components/image-gallery/enhance/enhance-image-utils';
import { VersionThumb } from '@modules/_shared/components/image-gallery/enhance/EnhanceVersionThumb';
import {
  buildListingImageVersions,
  getEnhanceSourceUrl,
  getListingVersionAction,
  isPropertyImageVersionConflict,
} from '@modules/_shared/components/image-gallery/enhance/imageVersionUtils';
import { Puller } from '@modules/_shared/components/puller';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { SlideUpTransition } from '@modules/_shared/components/SlideDialog';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';
import { useLocale } from '@shared/hooks/locale';

const mobileActionButtonSx: SxProps<Theme> = {
  flex: 1,
  bgcolor: 'background.paper',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'divider',
  borderRadius: 4,
  px: 2,
  py: 1.5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: '0 12px 40px -8px rgba(0,0,0,0.12)',
  minHeight: 52,
} as const;

type PromptInputProps = {
  prompt: string;
  onPromptChange: (value: string) => void;
  isEnhancing: boolean;
  disabled?: boolean;
  source: 'profile' | 'property';
  onSubmit: () => void;
  showBackButton?: boolean;
  onBack?: () => void;
  t: (key: string) => string;
};

const PromptInput = ({
  prompt,
  onPromptChange,
  isEnhancing,
  disabled = false,
  source,
  onSubmit,
  showBackButton = false,
  onBack,
  t,
}: PromptInputProps) => (
  <Box
    sx={{
      flexShrink: 0,
      px: { xs: 2, md: 4 },
      pt: 1,
      pb: { xs: 4, md: 3 },
      zIndex: 20,
    }}
  >
    <Box
      sx={{
        maxWidth: 800,
        mx: 'auto',
        p: 0.5,
        borderRadius: 4,
        bgcolor: theme => alpha(theme.palette.primary.main, 0.06),
        transition: 'background-color 0.2s ease',
        '&:focus-within': {
          bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
        },
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 1.5,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          px: { xs: 1, md: 1.75 },
          py: { xs: 1, md: 1.25 },
          minHeight: 56,

          boxShadow: theme =>
            `0 2px 16px ${alpha(theme.palette.common.black, 0.06)}`,

          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',

          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: theme =>
              `0 0 0 3px ${alpha(
                theme.palette.primary.main,
                0.2,
              )}, 0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
          },
        }}
      >
        {showBackButton && (
          <IconButton
            onClick={onBack}
            size="small"
            sx={{
              flexShrink: 0,
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <XIcon size={18} />
          </IconButton>
        )}
        <TextField
          multiline
          minRows={1}
          maxRows={2}
          placeholder={
            source === 'profile'
              ? t('Put me in front of a villa, fix my lighting...')
              : t('Add a blue sky, brighten the room, fix the lawn...')
          }
          value={prompt}
          onChange={e => onPromptChange(e.target.value)}
          disabled={isEnhancing || disabled}
          onKeyDown={e => {
            if (e.key !== 'Enter' || e.shiftKey || isEnhancing || disabled) {
              return;
            }
            e.preventDefault();
            onSubmit();
          }}
          variant="standard"
          sx={{
            flex: 1,
            minWidth: 0,
            mx: 0,
            '& .MuiInputBase-root': {
              alignItems: 'flex-start',
              marginTop: 0,
              marginBottom: 0,
              py: 0,
            },
            '& .MuiInputBase-input': {
              px: 0,
              py: 1.125,
              fontSize: '1rem',
              lineHeight: 1.5,
              letterSpacing: '-0.01em',
              color: 'text.primary',
              resize: 'none',
              '&::placeholder': {
                color: 'text.secondary',
                opacity: 0.55,
              },
            },
          }}
          slotProps={{
            input: { disableUnderline: true },
          }}
        />
        <IconButton
          onClick={onSubmit}
          disabled={!prompt.trim() || isEnhancing || disabled}
          size="small"
          color="primary"
          aria-label={t('Send')}
          sx={{
            flexShrink: 0,
            width: 44,
            height: 44,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            boxShadow: theme =>
              `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
            transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
            '&:hover': {
              bgcolor: 'primary.dark',
              boxShadow: theme =>
                `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
            },
            '&.Mui-disabled': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            },
          }}
        >
          {isEnhancing ? (
            <CircularProgress
              size={20}
              sx={{ color: 'primary.contrastText' }}
            />
          ) : (
            <ArrowUpIcon size={18} />
          )}
        </IconButton>
      </Stack>
    </Box>
  </Box>
);

type MobileActionBarProps = {
  mobileShowCustomInput: boolean;
  setMobileShowCustomInput: (value: boolean) => void;
  mobileDrawerOpen: boolean;
  setMobileDrawerOpen: (value: boolean) => void;
  isEnhancing: boolean;
  isVersionLoading: boolean;
  prompt: string;
  onPromptChange: (value: string) => void;
  onSubmit: () => void;
  source: 'profile' | 'property';
  categories: EnhanceImagePanelProps['categories'];
  activeCategory: string;
  setActiveCategory: (value: string) => void;
  actions: EnhancePanelAction[];
  subcategories: EnhanceSubcategory[];
  onActionSelected: (action: EnhancePanelAction) => void;
  t: (key: string) => string;
};

const MobileActionBar = ({
  mobileShowCustomInput,
  setMobileShowCustomInput,
  mobileDrawerOpen,
  setMobileDrawerOpen,
  isEnhancing,
  isVersionLoading,
  prompt,
  onPromptChange,
  onSubmit,
  source,
  categories,
  activeCategory,
  setActiveCategory,
  actions,
  subcategories,
  onActionSelected,
  t,
}: MobileActionBarProps) => (
  <>
    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
      {mobileShowCustomInput ? (
        <PromptInput
          prompt={prompt}
          onPromptChange={onPromptChange}
          isEnhancing={isEnhancing}
          disabled={isVersionLoading}
          source={source}
          onSubmit={onSubmit}
          showBackButton
          onBack={() => setMobileShowCustomInput(false)}
          t={t}
        />
      ) : (
        <Box
          sx={{
            flexShrink: 0,
            px: 2,
            pt: 1,
            pb: 4,
            zIndex: 20,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: 'center',
            }}
          >
            <Box
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ ...mobileActionButtonSx, gap: 1.5 }}
            >
              <SparklesIcon
                style={{ opacity: 0.8 }}
                size={18}
                color={'var(--mui-palette-primary-main)'}
              />
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                }}
              >
                {t('Presets')}
              </Typography>
            </Box>
            <Box
              onClick={() => setMobileShowCustomInput(true)}
              sx={{ ...mobileActionButtonSx, px: 1 }}
            >
              <Typography
                sx={{
                  color: 'text.primary',
                  fontWeight: 600,
                }}
              >
                {t('Custom')}
              </Typography>
            </Box>
          </Stack>
        </Box>
      )}
    </Box>

    <Drawer
      anchor="bottom"
      open={mobileDrawerOpen}
      onClose={() => setMobileDrawerOpen(false)}
      sx={{ zIndex: theme => theme.zIndex.modal + 10 }}
      slotProps={{
        paper: {
          sx: {
            height: '80vh',
            maxHeight: '80vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'grey.50',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          pt: 1.5,
          pb: 0.5,
          flexShrink: 0,
        }}
      >
        <Puller />
      </Box>
      <ActionPicker
        categories={categories}
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        actions={actions}
        subcategories={subcategories}
        onActionSelected={onActionSelected}
        disabled={isEnhancing || isVersionLoading}
      />
    </Drawer>
  </>
);

export const EnhanceImagePanel = ({
  open,
  onClose,
  image,
  onEnhance,
  onSaveImages,
  listingVersioning,
  source = 'property',
  maxImageDimension = 2048,
  actions,
  categories,
  subcategories,
}: EnhanceImagePanelProps) => {
  const { t } = useLocale();
  const { track } = useAnalytics();
  const [draftVersions, setDraftVersions] = useState<EnhancedVersion[]>([]);
  const [prompt, setPrompt] = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const isEnhancing = pendingCount > 0;
  const [viewingVersionId, setViewingVersionId] = useState('');
  const [pendingViewingVersionId, setPendingViewingVersionId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileShowCustomInput, setMobileShowCustomInput] = useState(false);
  const isMountedRef = useRef(true);
  const currentImageIdRef = useRef(image?.id);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const currentImageId = listingVersioning?.currentImageId ?? image?.id ?? '';
  const listingSavedVersions = listingVersioning?.savedVersions;
  const savedVersions = useMemo(
    () =>
      listingSavedVersions
        ? listingSavedVersions
        : image
        ? buildListingImageVersions({ id: currentImageId, url: image.url }, [])
        : [],
    [currentImageId, image, listingSavedVersions],
  );
  const displayedVersions = useMemo(
    () => [...savedVersions, ...draftVersions],
    [draftVersions, savedVersions],
  );
  const currentVersionId =
    savedVersions.find(version => version.imageId === currentImageId)?.id ??
    savedVersions[0]?.id ??
    '';
  const initialVersionId =
    savedVersions.find(
      version => version.imageId === listingVersioning?.initialVersionImageId,
    )?.id ?? currentVersionId;
  const viewedVersion =
    displayedVersions.find(version => version.id === viewingVersionId) ??
    displayedVersions.find(version => version.id === initialVersionId) ??
    displayedVersions.find(version => version.id === currentVersionId);
  const pendingViewedVersion = displayedVersions.find(
    version => version.id === pendingViewingVersionId,
  );
  const isVersionLoading = pendingViewedVersion != null;

  useEffect(() => {
    currentImageIdRef.current = image?.id;
    setDraftVersions(prev => {
      prev.forEach(v => URL.revokeObjectURL(v.blobUrl));
      return [];
    });
    setPrompt('');
    setPendingCount(0);
    setViewingVersionId('');
    setPendingViewingVersionId('');
    setIsSaving(false);
    setError(null);
    setActiveCategory('all');
    setMobileDrawerOpen(false);
    setMobileShowCustomInput(false);
  }, [image?.id, open]);

  useEffect(() => {
    setViewingVersionId(previousVersionId =>
      displayedVersions.some(version => version.id === previousVersionId)
        ? previousVersionId
        : open
        ? initialVersionId
        : currentVersionId,
    );
    setPendingViewingVersionId(previousVersionId =>
      displayedVersions.some(version => version.id === previousVersionId)
        ? previousVersionId
        : '',
    );
  }, [currentVersionId, displayedVersions, initialVersionId, open]);

  useEffect(() => {
    return () => {
      setDraftVersions(prev => {
        prev.forEach(v => URL.revokeObjectURL(v.blobUrl));
        return [];
      });
    };
  }, []);

  const handleSend = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!image || !trimmed || isVersionLoading) {
        return;
      }

      const requestImageId = currentImageIdRef.current;
      if (!viewedVersion) {
        return;
      }

      const sourceSrc =
        viewedVersion.kind === 'draft'
          ? `data:image/png;base64,${viewedVersion.imageData}`
          : getEnhanceSourceUrl(viewedVersion.imageUrl, maxImageDimension);

      if (!sourceSrc) {
        setError(t('Version unavailable'));
        return;
      }

      setPrompt('');
      setError(null);
      setPendingCount(c => c + 1);

      try {
        const sourceImageData = await resizeImageToBase64(
          sourceSrc,
          maxImageDimension,
        );
        const result = await onEnhance(trimmed, sourceImageData);
        const blobUrl = await base64ToBlobUrl(result.image_data);

        if (
          !isMountedRef.current ||
          currentImageIdRef.current !== requestImageId
        ) {
          URL.revokeObjectURL(blobUrl);
          return;
        }

        const newVersion: EnhancedVersion = {
          kind: 'draft',
          id: uuidv4(),
          imageData: result.image_data,
          blobUrl,
          prompt: trimmed,
          sourceImageId:
            viewedVersion.kind === 'saved' ? viewedVersion.imageId : null,
          sourceVersion:
            viewedVersion.kind === 'draft'
              ? 'draft_ai'
              : viewedVersion.id === savedVersions[0]?.id
              ? 'original'
              : 'saved_ai',
        };

        setDraftVersions(prev => {
          const next = [...prev, newVersion];
          setViewingVersionId(newVersion.id);
          return next;
        });
      } catch (e) {
        console.error('Error enhancing image:', e);
        setError(t('Enhancement failed. Try a different prompt.'));
      } finally {
        setPendingCount(c => c - 1);
      }
    },
    [
      image,
      isVersionLoading,
      maxImageDimension,
      onEnhance,
      savedVersions,
      t,
      viewedVersion,
    ],
  );

  const handleActionSelected = useCallback(
    (action: EnhancePanelAction) => {
      track(ANALYTICS_EVENTS.AI_ENHANCE_PRESET_CLICKED, {
        preset_id: action.id,
        preset_label: action.label,
        category: action.category,
        source,
      });
      handleSend(action.getPrompt?.() ?? action.prompt);
      setMobileDrawerOpen(false);
    },
    [track, source, handleSend],
  );

  const versionAction = viewedVersion
    ? listingVersioning
      ? getListingVersionAction(viewedVersion, currentImageId)
      : viewedVersion.kind === 'draft'
      ? 'save'
      : 'current'
    : 'current';

  const handleVersionAction = useCallback(async () => {
    if (!viewedVersion || isVersionLoading || versionAction === 'current') {
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      if (viewedVersion.kind === 'draft') {
        if (listingVersioning) {
          await listingVersioning.onSaveVersion(
            listingVersioning.propertyImageId,
            {
              imageData: viewedVersion.imageData,
              prompt: viewedVersion.prompt,
              sourceImageId: viewedVersion.sourceImageId,
              expectedCurrentImageId: listingVersioning.currentImageId,
            },
          );
          track(ANALYTICS_EVENTS.AI_ENHANCE_SAVED, {
            images_count: 1,
            versions_generated: draftVersions.length,
            source: 'property',
            property_image_id: listingVersioning.propertyImageId,
            source_version: viewedVersion.sourceVersion,
            saved_version_count: savedVersions.length + 1,
          });
        } else {
          if (!onSaveImages) {
            return;
          }
          await onSaveImages([viewedVersion.imageData]);
          track(ANALYTICS_EVENTS.AI_ENHANCE_SAVED, {
            images_count: 1,
            versions_generated: draftVersions.length,
            source,
          });
        }
      } else if (listingVersioning) {
        await listingVersioning.onUseVersion(
          listingVersioning.propertyImageId,
          viewedVersion.imageId,
          listingVersioning.currentImageId,
        );
        track(ANALYTICS_EVENTS.AI_ENHANCE_VERSION_RESTORED, {
          property_image_id: listingVersioning.propertyImageId,
          target_version:
            viewedVersion.id === savedVersions[0]?.id ? 'original' : 'saved_ai',
        });
      }
      onClose();
    } catch (actionError) {
      console.error('Error updating image version:', actionError);
      setError(
        isPropertyImageVersionConflict(actionError)
          ? t('The photo changed in another session. Please try again.')
          : viewedVersion.kind === 'draft'
          ? t('Failed to save images. Please try again.')
          : t('Failed to use this version. Please try again.'),
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    draftVersions.length,
    isVersionLoading,
    listingVersioning,
    onClose,
    onSaveImages,
    savedVersions,
    source,
    t,
    track,
    versionAction,
    viewedVersion,
  ]);

  const showVersionAction =
    listingVersioning != null || versionAction === 'save';
  let versionActionLabel: string;
  if (versionAction === 'save') {
    versionActionLabel = listingVersioning
      ? t('Save version')
      : t('Save this version');
  } else if (versionAction === 'restore') {
    versionActionLabel = t('Use this version');
  } else {
    versionActionLabel = t('Current version');
  }

  const renderPreviewImage = () => {
    const baseImgSx = {
      maxWidth: '100%',
      maxHeight: '100%',
      width: '100%',
      objectFit: 'contain' as const,
      display: 'block',
      transition: 'filter 0.5s ease',
    };

    return (
      <Box
        sx={{
          width: '100%',
          flexGrow: 1,
          position: 'relative',
        }}
      >
        {[viewedVersion, pendingViewedVersion].map(version => {
          if (!version) {
            return null;
          }

          const src =
            version.kind === 'draft'
              ? version.blobUrl
              : getEnhanceSourceUrl(version.imageUrl, maxImageDimension);
          if (!src) {
            return null;
          }

          const isPending = version.id === pendingViewedVersion?.id;
          return (
            <Box
              key={version.id}
              component="img"
              src={src}
              alt=""
              onLoad={() => {
                if (isPending) {
                  setViewingVersionId(version.id);
                  setPendingViewingVersionId('');
                }
              }}
              onError={() => {
                if (isPending) {
                  setPendingViewingVersionId('');
                  setError(t('Version unavailable'));
                }
              }}
              sx={{
                ...baseImgSx,
                position: isPending ? 'absolute' : undefined,
                visibility: isPending ? 'hidden' : undefined,
                filter:
                  isEnhancing && !isPending
                    ? 'brightness(0.9) blur(4px)'
                    : 'none',
              }}
            />
          );
        })}
        {isEnhancing && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 32,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              bgcolor: 'rgba(20, 20, 20, 0.85)',
              px: 3,
              py: 1.5,
              borderRadius: 8,
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(16px)',
              pointerEvents: 'none',
              zIndex: 10,
              border: '1px solid rgba(255, 255, 255, 0.08)',
              '@keyframes pulseSparkle': {
                '0%, 100%': { transform: 'scale(1)', opacity: 0.6 },
                '50%': { transform: 'scale(1.2)', opacity: 1 },
              },
            }}
          >
            <SparklesIcon
              style={{ animation: 'pulseSparkle 2s ease-in-out infinite' }}
              size={18}
              color={'#fff'}
            />
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: '#fff', letterSpacing: 0.3 }}
            >
              {t('Enhancing...')}
            </Typography>
          </Box>
        )}
        {isVersionLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 10,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
      </Box>
    );
  };

  const trackCustomPrompt = () => {
    if (prompt.trim()) {
      track(ANALYTICS_EVENTS.AI_ENHANCE_CUSTOM_PROMPT, {
        prompt_text: prompt.trim(),
        source,
      });
    }
  };

  const submitPrompt = () => {
    trackCustomPrompt();
    handleSend(prompt);
    setMobileDrawerOpen(false);
  };

  if (!image) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        slots={{
          transition: SlideUpTransition,
        }}
        slotProps={{
          paper: {
            sx: {
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        {/* ── Header ── */}
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 4,
            py: 2,
            minHeight: 70,
            borderBottomWidth: 1,
            borderBottomStyle: 'solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: 'center',
            }}
          >
            <SparklesIcon size={18} color={'var(--mui-palette-primary-main)'} />
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
              }}
            >
              {t('Enhance with AI')}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            {showVersionAction && (
              <LoadingButton
                onClick={handleVersionAction}
                loading={isSaving}
                loadingPosition="start"
                variant="contained"
                color="primary"
                disabled={isVersionLoading || versionAction === 'current'}
                startIcon={<SaveIcon />}
                sx={{ textTransform: 'none' }}
              >
                {versionActionLabel}
              </LoadingButton>
            )}
            <IconButton onClick={onClose} size="small">
              <XIcon size={18} />
            </IconButton>
          </Stack>
        </Stack>

        {error && <RASnackbar message={error} onClose={() => setError(null)} />}

        {/* ── Main content: three columns ── */}
        <Box
          sx={{
            display: 'flex',
            flexGrow: 1,
            overflow: 'hidden',
            flexDirection: { xs: 'column', md: 'row' },
            bgcolor: 'grey.50',
          }}
        >
          {/* ── LEFT: Version strip + selected image + prompt ── */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              borderRightWidth: { xs: 0, md: 1 },
              borderBottomWidth: { xs: 1, md: 0 },
              borderRightStyle: 'solid',
              borderBottomStyle: 'solid',
              borderColor: 'divider',
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            {/* Vertical/Horizontal version strip */}
            <Box
              sx={{
                flexShrink: 0,
                width: { xs: '100%', md: 182 },
                height: { xs: 'auto', md: '100%' },
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' },
                gap: 2,
                p: 2,
                overflowY: { xs: 'hidden', md: 'auto' },
                overflowX: { xs: 'auto', md: 'hidden' },
                borderRightWidth: { xs: 0, md: 1 },
                borderBottomWidth: { xs: 1, md: 0 },
                borderRightStyle: 'solid',
                borderBottomStyle: 'solid',
                borderColor: 'divider',
                zIndex: 1,
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
              }}
            >
              {displayedVersions.map((version, index) => {
                const isCurrent =
                  version.kind === 'saved' &&
                  version.imageId === currentImageId;
                const isUnavailable =
                  version.kind === 'saved' && version.imageUrl == null;
                const draftNumber =
                  version.kind === 'draft'
                    ? draftVersions.findIndex(
                        draft => draft.id === version.id,
                      ) + 1
                    : null;
                const label = isUnavailable
                  ? t('Version unavailable')
                  : [
                      version.kind === 'draft'
                        ? listingVersioning
                          ? `${t('Unsaved')} · v${draftNumber}`
                          : `v${draftNumber}`
                        : index === 0
                        ? t('Original')
                        : `v${index}`,
                      isCurrent && listingVersioning ? t('Current') : null,
                    ]
                      .filter(Boolean)
                      .join(' · ');

                return (
                  <Box
                    key={version.id}
                    sx={{ width: { xs: 120, md: '100%' }, flexShrink: 0 }}
                  >
                    <VersionThumb
                      active={
                        (pendingViewedVersion ?? viewedVersion)?.id ===
                        version.id
                      }
                      disabled={isUnavailable}
                      onClick={() => {
                        if (
                          pendingViewedVersion?.id === version.id ||
                          viewedVersion?.id === version.id
                        ) {
                          setPendingViewingVersionId('');
                        } else {
                          setPendingViewingVersionId(version.id);
                        }
                      }}
                      label={label}
                    >
                      {isUnavailable ? (
                        <Skeleton
                          variant="rectangular"
                          width="100%"
                          height="100%"
                        />
                      ) : version.kind === 'draft' ? (
                        <img
                          src={version.blobUrl}
                          alt=""
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <Image
                          src={version.imageUrl ?? ''}
                          options={{ w: 380, h: 230, c: 'fill', f: 'jpg' }}
                          objectFit="cover"
                          css={{ width: '100%', height: '100%' }}
                        />
                      )}
                    </VersionThumb>
                  </Box>
                );
              })}

              {/* Generating placeholder thumbnails — one per pending request */}
              {Array.from({ length: pendingCount }, (_, i) => (
                <Box
                  key={`pending-${i}`}
                  sx={{ width: { xs: 120, md: '100%' }, flexShrink: 0 }}
                >
                  <VersionThumb
                    active={i === 0}
                    disabled
                    label={t('Generating...')}
                  >
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height="100%"
                      animation="wave"
                    />
                  </VersionThumb>
                </Box>
              ))}
            </Box>

            {/* Image preview + prompt column (input pinned to bottom) */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                minHeight: 0,
                position: 'relative',
                alignSelf: 'stretch',
              }}
            >
              {/* Selected image preview — fills space above controls */}
              <Box
                sx={{
                  flex: '1 1 0',
                  position: 'relative',
                  overflow: 'hidden',
                  minWidth: 0,
                  minHeight: { xs: 160, md: 220 },
                  maxHeight: { xs: '50vh', md: 'min(620px, 75vh)' },
                  p: 2,
                  display: 'flex',
                }}
              >
                {renderPreviewImage()}
              </Box>

              {/* Presets / custom / prompt — stays at bottom of column */}
              <Box
                sx={{
                  flexShrink: 0,
                  width: '100%',
                  mt: 'auto',
                }}
              >
                <MobileActionBar
                  mobileShowCustomInput={mobileShowCustomInput}
                  setMobileShowCustomInput={setMobileShowCustomInput}
                  mobileDrawerOpen={mobileDrawerOpen}
                  setMobileDrawerOpen={setMobileDrawerOpen}
                  isEnhancing={isEnhancing}
                  isVersionLoading={isVersionLoading}
                  prompt={prompt}
                  onPromptChange={setPrompt}
                  onSubmit={submitPrompt}
                  source={source}
                  categories={categories}
                  activeCategory={activeCategory}
                  setActiveCategory={setActiveCategory}
                  actions={actions}
                  subcategories={subcategories}
                  onActionSelected={handleActionSelected}
                  t={t}
                />

                {/* Desktop prompt input */}
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <PromptInput
                    prompt={prompt}
                    onPromptChange={setPrompt}
                    isEnhancing={isEnhancing}
                    disabled={isVersionLoading}
                    source={source}
                    onSubmit={submitPrompt}
                    t={t}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ── RIGHT: Action picker ── */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flex: '0 0 400px',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            <ActionPicker
              categories={categories}
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
              actions={actions}
              subcategories={subcategories}
              onActionSelected={handleActionSelected}
              disabled={isEnhancing || isVersionLoading}
            />
          </Box>
        </Box>
      </Dialog>
    </>
  );
};
